import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { Spinner } from '@usealloy/component-library';

import { ajaxFetch, buildURLParamsString } from '~/helpers/api';
import { formatTimestamp, splitOnNewlines } from '~/helpers/formatting';
import { routes } from '~/helpers/routes';
import { useFetchedUserContext } from '~/helpers/hooks';

import TokenLinksOnNotesModal from './TokenLinksOnNotesModal';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';


const NotesListPropTypes = {
  small: PropTypes.bool,
  notes: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      noteHeader: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
      ]),
      text: PropTypes.string,
      agent_email: PropTypes.string,
      agent_name: PropTypes.string,
      agent_first_name: PropTypes.string,
      agent_last_name: PropTypes.string,
      created_at: PropTypes.number,
      formatted_created_at: PropTypes.string,
      formatted_timestamp: PropTypes.string,
      note_id: PropTypes.string,
    })),
    PropTypes.bool,
  ]),
  rowKey: PropTypes.string,
  rowId: PropTypes.number,
};

const NotesListDefaultProps = {
  small: false,
  notes: false,
  rowKey: '',
  rowId: 0,
};

export default function NotesList({
  notes, rowKey, rowId, small,
}) {
  const context = useFetchedUserContext();
  const [notesList, setNotesList] = useState(notes);
  const { feature_flags: featureFlags } = context;

  useEffect(() => {
    const url = routes.notes;
    if (!notes && rowKey && rowId) {
      const params = buildURLParamsString({ key: rowKey, row_id: rowId });
      ajaxFetch(`${url}${params}`).then(data => {
        if (data.notes) setNotesList(data.notes);
      });
    }
  }, [notes, rowKey, rowId]);

  return (
    <div className="notes-wrapper">
      {notesList ? notesList.map(note => {
        const fullTimestamp = formatTimestamp(note.created_at);
        const formattedTimestamp = (
          fullTimestamp ?? splitOnNewlines(`${note.formatted_timestamp ?? ''}`.concat(
            get(note, 'formatted_created_at.length')
              ? `\n${note.formatted_created_at}`
              : '',
          ))
        );

        const id = `note-${note.note_id}`;
        const agentShowingName = note.agent_name || (
          note.agent_first_name && note.agent_last_name
            ? `${note.agent_first_name} ${note.agent_last_name}`
            : ''
        );

        const agentShowingId = `agent-name-${note.note_id}-${rowId}`;

        return (
          <div key={note.note_id} className="note">
            {/* remove feature flag and old modal after all done */}
            {(featureFlags && featureFlags['eval-notes-and-docs'])
              ? (
                <div className="row flex-nowrap overflow-hidden">
                  {formattedTimestamp ? (
                    <div className="col-2">
                      <span className="flex start tiny-header-space">
                        <i id={id} className="far fa-fw fa-clock light-grey" />
                        <span>{formattedTimestamp}</span>
                        <Tooltip
                          target={id}
                          placement="right"
                          delay={0}
                          modifiers={tooltipZIndexModifiers}
                        >
                          {fullTimestamp}
                        </Tooltip>
                      </span>
                    </div>
                  ) : null}
                  <div className={small && formattedTimestamp ? 'col-6' : 'col-7'}>
                    {note.noteHeader && (
                      <span className="bold">
                        {note.noteHeader}
                      </span>
                    )}
                    {splitOnNewlines(note.text || '')}
                  </div>

                  <div className="col text-right text-nowrap">
                    {note.evaluation_token && (
                      <TokenLinksOnNotesModal
                        token={note.evaluation_token}
                        noteId={note.note_id}
                      />
                    )}
                  </div>

                  <div className="col text-nowrap">
                    <span id={agentShowingId}>
                      {(
                        agentShowingName.length < 10
                          ? agentShowingName
                          : `${agentShowingName.slice(0, 10)}...`
                      )}
                    </span>
                    <Tooltip
                      target={agentShowingId}
                      delay={0}
                      modifiers={tooltipZIndexModifiers}
                    >
                      {agentShowingName}
                    </Tooltip>
                  </div>
                </div>
              )
              : (
                <div className="row">
                  {formattedTimestamp ? (
                    <div className="col-3">
                      <span className="flex start tiny-header-space">
                        <i id={id} className="far fa-fw fa-clock light-grey" />
                        <span>{formattedTimestamp}</span>
                        <Tooltip
                          target={id}
                          placement="right"
                          delay={0}
                          modifiers={tooltipZIndexModifiers}
                        >
                          {fullTimestamp}
                        </Tooltip>
                      </span>
                    </div>
                  ) : null}

                  <div className={small && formattedTimestamp ? 'col-6' : 'col-7'}>
                    {note.noteHeader && <span className="bold">{note.noteHeader}</span>}
                    {splitOnNewlines(note.text || '')}
                  </div>

                  <div className="col">
                    <span>
                      {agentShowingName}
                    </span>
                  </div>
                </div>
              )}

          </div>
        );
      }) : (
        <div className="note">
          <div className="row">
            <div className="col text-center">
              <p>
                <Spinner />
                {' loading...'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

NotesList.propTypes = NotesListPropTypes;
NotesList.defaultProps = NotesListDefaultProps;
